import { Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';
import { usePermissions } from '@shared/hooks/usePermissions';
import { UnauthorizedPage } from '@shared/router/UnauthorizedPage';
import { RouteSplashScreen } from '@shared/router/RouteSplashScreen';

export interface PermissionGuardProps {
  /** Single permission required to enter the route. */
  permission?: string;
  /** Any one of these grants access. */
  anyOf?: readonly string[];
  /** All of these are required. */
  allOf?: readonly string[];
  /** Route subtree; omit to render the nested `<Outlet />`. */
  children?: ReactNode;
}

/**
 * Authorization gate for route subtrees.
 *
 * Compose it *inside* `ProtectedRoute`: authentication is already settled by
 * then, so an unauthorized user sees the Access Denied page instead of being
 * redirected — which is what keeps this loop-free. Hiding a sidebar link is
 * never enough on its own; every protected route carries its own guard.
 *
 * The backend re-checks each request; this only controls UI reachability.
 */
export function PermissionGuard({ permission, anyOf, allOf, children }: PermissionGuardProps) {
  const { can, canAny, canAll, isReady } = usePermissions();

  // Authorization has not been applied yet (in-flight bootstrap or refresh).
  // Hold the splash rather than flashing Access Denied and shifting layout.
  if (!isReady) return <RouteSplashScreen />;

  const authorized =
    (permission ? can(permission) : true) &&
    (anyOf && anyOf.length > 0 ? canAny(anyOf) : true) &&
    (allOf && allOf.length > 0 ? canAll(allOf) : true);

  if (!authorized) return <UnauthorizedPage />;
  return <>{children ?? <Outlet />}</>;
}
